import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTranslation } from 'react-i18next';
import ActionSettingsLayout from '../ActionSettingsHeader';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';
import TagEditor from '../Widgets/TagEditor';
import TagsDropdown from '../TagsDropdown';

const toBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (value === undefined || value === null || value === '') {
    return fallback;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  const normalized = String(value).trim().toLowerCase();
  if (['1', 'true', 'yes', 'y', 'on'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'n', 'off'].includes(normalized)) {
    return false;
  }
  return fallback;
};

const normalizeNumberString = (value, fallback = '') => {
  if (value === undefined || value === null) {
    return fallback;
  }
  const normalized = String(value).trim();
  return normalized;
};

const normalizeReasoningEffort = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['none', 'minimal', 'low', 'medium', 'high', 'xhigh'].includes(normalized)) {
    return normalized;
  }
  return 'none';
};

const normalizePdfAutoRotatePreset = (value, fallback = 'off') => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['off', 'fast', 'balanced', 'quality', 'llm'].includes(normalized) ? normalized : fallback;
};

const DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS = {
  textScore: '0.95',
  layoutScore: '0.93',
  alignmentScore: '0.93',
  confidence: '0.85',
};

const DEFAULT_PDF_TEXT_MAX_ATTEMPTS = 3;
const DEFAULT_LEGAL_FIX_ATTEMPTS = 5;

const normalizeAcceptanceThresholdString = (value, fallback = '0') => {
  if (value === undefined || value === null || value === '') {
    return fallback;
  }

  const normalized = String(value).trim();
  if (!normalized) {
    return fallback;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  const clamped = Math.min(1, Math.max(0, parsed));
  return String(clamped);
};

const ORIENTATION_PRESETS = {
  fast: {
    useEasyocr: false,
    previewMaxDimension: '1024',
    fineAngleRange: '3.0',
    fineAngleStep: '1.0',
  },
  balanced: {
    useEasyocr: true,
    previewMaxDimension: '1600',
    fineAngleRange: '7.0',
    fineAngleStep: '0.5',
  },
  quality: {
    useEasyocr: true,
    previewMaxDimension: '2048',
    fineAngleRange: '10.0',
    fineAngleStep: '0.25',
  },
  llm: {
    useEasyocr: false,
    previewMaxDimension: '640',
    fineAngleRange: '0.0',
    fineAngleStep: '0.0',
  },
};

const resolveOrientationPreset = (useEasyocr, previewMaxDimension, fineAngleRange, fineAngleStep) => {
  const normalizedPreview = normalizeNumberString(previewMaxDimension, '');
  const normalizedRange = normalizeNumberString(fineAngleRange, '');
  const normalizedStep = normalizeNumberString(fineAngleStep, '');

  for (const [presetKey, preset] of Object.entries(ORIENTATION_PRESETS)) {
    if (
      Boolean(useEasyocr) === preset.useEasyocr
      && normalizedPreview === preset.previewMaxDimension
      && normalizedRange === preset.fineAngleRange
      && normalizedStep === preset.fineAngleStep
    ) {
      return presetKey;
    }
  }

  return 'custom';
};

const ActionsConvertPdfToDocx = ({
  caseid,
  tags,
  settingId,
  onSaveStatusChange,
  initialScope,
  actionType = 'convertpdftodocx',
  titleOverride = null,
}) => {
  const { t } = useTranslation();
  const isProjectScope = initialScope === 'project';
  const defaultSourceMode = isProjectScope ? 'other' : 'current';
  const resolvedActionType = actionType || 'convertpdftodocx';
  const isHtmlAction = resolvedActionType === 'convertpdftohtml';
  const isPdfTextAction = resolvedActionType === 'convertpdfimagetopdftext';
  const isDocumentLegalPdfToDocxAction = resolvedActionType === 'documentlegalpdftodocx';
  const usesPdfTextPipelineSettings = isPdfTextAction || isDocumentLegalPdfToDocxAction;
  const isRotatePdfOcrAction = resolvedActionType === 'rotatepdfimageocrpdf';
  const resolvedTitle = titleOverride
    || (isRotatePdfOcrAction
      ? t('workflow.rotatepdfimageocrpdf', 'Make the page straight')
      : isDocumentLegalPdfToDocxAction
        ? t('workflow.documentlegalpdftodocx', 'Legal Document - PDF to DOCX')
        : t('workflow.convertpdftodocx', 'Convert PDF to DOCX'));
  const targetSectionTitle = isHtmlAction
    ? t('pdf_to_html.target_title', 'Output Directory')
    : isDocumentLegalPdfToDocxAction
      ? t('pdf_to_docx.target_title', 'Target DOCX')
      : isPdfTextAction
        ? t('pdf_image_to_pdf_text.target_title', 'Target Text PDF')
        : isRotatePdfOcrAction
        ? t('pdf_rotate_ocr.target_title', 'New straight file')
    : t('pdf_to_docx.target_title', 'Target DOCX');
  const targetPathPlaceholder = isHtmlAction
    ? t('pdf_to_html.target_path_placeholder', '/html_debug')
    : isDocumentLegalPdfToDocxAction
      ? t('pdf_to_docx.target_path_placeholder', '/{filename}_ocr.docx')
      : isPdfTextAction
        ? t('pdf_image_to_pdf_text.target_path_placeholder', '/{filename}_text.pdf')
        : isRotatePdfOcrAction
        ? t('pdf_rotate_ocr.target_path_placeholder', '/{filename}_rotated.pdf')
    : t('pdf_to_docx.target_path_placeholder', '/{filename}_ocr.docx');
  const targetPathHelp = isHtmlAction
    ? t(
        'pdf_to_html.target_path_help',
        'Output directory for the generated HTML pages. Use tags like {filename} for dynamic naming.'
      )
    : isDocumentLegalPdfToDocxAction
      ? t(
          'pdf_to_docx.target_path_help',
          'Output path for the generated DOCX file. Use tags like {filename} for dynamic naming.'
        )
      : isPdfTextAction
        ? t(
            'pdf_image_to_pdf_text.target_path_help',
            'Output path for the generated searchable text PDF. Use tags like {filename} for dynamic naming.'
          )
        : isRotatePdfOcrAction
        ? t(
            'pdf_rotate_ocr.target_path_help',
            'Where to save the new straight file. Use {filename} to make the name change by itself.'
          )
    : t(
        'pdf_to_docx.target_path_help',
        'Output path for the generated DOCX file. Use tags like {filename} for dynamic naming.'
      );
  const targetPathRequiredError = isHtmlAction
    ? t('pdf_to_html.target_path_error_required', 'An output directory is required before saving.')
    : isDocumentLegalPdfToDocxAction
      ? t('pdf_to_docx.target_path_error_required', 'A target DOCX path is required before saving.')
      : isPdfTextAction
        ? t('pdf_image_to_pdf_text.target_path_error_required', 'A target PDF path is required before saving.')
        : isRotatePdfOcrAction
        ? t('pdf_rotate_ocr.target_path_error_required', 'Please choose where to save the new straight file.')
    : t('pdf_to_docx.target_path_error_required', 'A target DOCX path is required before saving.');

  const [sourceMode, setSourceMode] = useState(defaultSourceMode);
  const [sourceFilePath, setSourceFilePath] = useState('');
  const [targetPath, setTargetPath] = useState('');
  const [renderDpi, setRenderDpi] = useState(isDocumentLegalPdfToDocxAction ? '300' : '200');
  const [maxPages, setMaxPages] = useState('');
  const [endPage, setEndPage] = useState('');
  const [useEasyocr, setUseEasyocr] = useState(true);
  const [previewMaxDimension, setPreviewMaxDimension] = useState('1600');
  const [fineAngleRange, setFineAngleRange] = useState('7.0');
  const [fineAngleStep, setFineAngleStep] = useState('0.5');
  const [orientationPreset, setOrientationPreset] = useState('balanced');
  const [includePageImages, setIncludePageImages] = useState(false);
  const [includeInitialPageCrops, setIncludeInitialPageCrops] = useState(true);
  const [saveDebugHtmlPages, setSaveDebugHtmlPages] = useState(false);
  const [parallelPages, setParallelPages] = useState(isDocumentLegalPdfToDocxAction ? '8' : '1');
  const defaultMaxAttempts = isDocumentLegalPdfToDocxAction
    ? String(DEFAULT_LEGAL_FIX_ATTEMPTS)
    : String(DEFAULT_PDF_TEXT_MAX_ATTEMPTS);
  const [maxAttempts, setMaxAttempts] = useState(usesPdfTextPipelineSettings ? defaultMaxAttempts : '');
  const [skipIfPdfHasText, setSkipIfPdfHasText] = useState(isDocumentLegalPdfToDocxAction ? false : true);
  const [autoOrientationPreset, setAutoOrientationPreset] = useState('off');
  const [reasoningEffort, setReasoningEffort] = useState('none');
  const [finalReasoningEffort, setFinalReasoningEffort] = useState(isDocumentLegalPdfToDocxAction ? 'none' : 'medium');
  const [minTextScore, setMinTextScore] = useState(isDocumentLegalPdfToDocxAction ? DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.textScore : '');
  const [minLayoutScore, setMinLayoutScore] = useState(isDocumentLegalPdfToDocxAction ? DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.layoutScore : '');
  const [minAlignmentScore, setMinAlignmentScore] = useState(isDocumentLegalPdfToDocxAction ? DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.alignmentScore : '');
  const [minConfidence, setMinConfidence] = useState(isDocumentLegalPdfToDocxAction ? DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.confidence : '');
  const [sourcePathError, setSourcePathError] = useState('');
  const [targetPathError, setTargetPathError] = useState('');
  const [sourceBrowseDisabled, setSourceBrowseDisabled] = useState(false);
  const [targetBrowseDisabled, setTargetBrowseDisabled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState('');

  const sourceRef = useRef(null);
  const targetRef = useRef(null);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);
  const isOrientationCustom = isRotatePdfOcrAction && orientationPreset === 'custom';
  const showMaxPages = !isDocumentLegalPdfToDocxAction;
  const showEndPage = isDocumentLegalPdfToDocxAction;
  const showDocumentLegalProcessingOptions = isDocumentLegalPdfToDocxAction;
  const showDocumentLegalAcceptanceThresholds = isDocumentLegalPdfToDocxAction;
  const showMaxAttempts = isPdfTextAction || isDocumentLegalPdfToDocxAction;

  const applyOrientationPreset = useCallback((presetKey) => {
    const preset = ORIENTATION_PRESETS[presetKey];
    if (!preset) {
      return;
    }

    setOrientationPreset(presetKey);
    setUseEasyocr(preset.useEasyocr);
    setPreviewMaxDimension(preset.previewMaxDimension);
    setFineAngleRange(preset.fineAngleRange);
    setFineAngleStep(preset.fineAngleStep);
  }, []);

  const {
    isEnabled,
    setIsEnabled,
    isSaveDisabled,
    conditions,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleConditionsChange,
    handleSave: baseHandleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleCloseStatusWindow,
    updateSaveDisabledState,
  } = useActionSettings({
    settingId,
    caseid,
    actionType: resolvedActionType,
    onSaveStatusChange,
    customComparison: (initial) => {
      if (String(initial?.id || '').startsWith('temp_')) {
        return false;
      }

      const initialSourceMode = String(initial?.sourceMode || initial?.source_mode || defaultSourceMode);
      const initialSourceFilePath = String(initial?.sourceFilePath || initial?.sourcePath || '');
      const initialTargetPath = String(initial?.targetPath || initial?.target || '');
      const initialRenderDpi = normalizeNumberString(initial?.renderDpi ?? initial?.dpi, '200');
      const initialMaxPages = normalizeNumberString(initial?.maxPages ?? initial?.max_pages, '');
      const initialEndPage = normalizeNumberString(initial?.endPage ?? initial?.end_page, '');
      const initialOrientationPresetRaw = String(initial?.orientationPreset || initial?.orientation_preset || '').trim().toLowerCase();
      const initialUseEasyocr = toBoolean(initial?.useEasyocr ?? initial?.use_easyocr, true);
      const initialPreviewMaxDimension = normalizeNumberString(
        initial?.previewMaxDimension ?? initial?.preview_max_dimension,
        '1600'
      );
      const initialFineAngleRange = normalizeNumberString(initial?.fineAngleRange ?? initial?.fine_angle_range, '7.0');
      const initialFineAngleStep = normalizeNumberString(initial?.fineAngleStep ?? initial?.fine_angle_step, '0.5');
      const initialIncludePageImages = toBoolean(initial?.includePageImages ?? initial?.include_page_images, false);
      const initialIncludeInitialPageCrops = toBoolean(
        initial?.includeInitialPageCrops ?? initial?.include_initial_page_crops,
        true
      );
      const initialSaveDebugHtmlPages = toBoolean(initial?.saveDebugHtmlPages ?? initial?.save_debug_html_pages, false);
      const initialParallelPages = normalizeNumberString(
        initial?.parallelPages ?? initial?.parallel_pages,
        isDocumentLegalPdfToDocxAction ? '8' : '1'
      ) || (isDocumentLegalPdfToDocxAction ? '8' : '1');
      const initialMaxAttempts = normalizeNumberString(
        initial?.fixAttempts
          ?? initial?.fix_attempts
          ?? initial?.maxAttempts
          ?? initial?.max_attempts,
        isDocumentLegalPdfToDocxAction ? String(DEFAULT_LEGAL_FIX_ATTEMPTS) : String(DEFAULT_PDF_TEXT_MAX_ATTEMPTS)
      ) || (isDocumentLegalPdfToDocxAction ? String(DEFAULT_LEGAL_FIX_ATTEMPTS) : String(DEFAULT_PDF_TEXT_MAX_ATTEMPTS));
      const initialSkipIfPdfHasText = toBoolean(initial?.skipIfPdfHasText ?? initial?.skip_if_pdf_has_text, isDocumentLegalPdfToDocxAction ? false : true);
      const initialAutoOrientOcr = toBoolean(initial?.autoOrientOcr ?? initial?.auto_orient_ocr, false);
      const initialAutoOrientationPreset = normalizePdfAutoRotatePreset(
        initial?.orientationPreset ?? initial?.orientation_preset,
        initialAutoOrientOcr ? 'balanced' : 'off'
      );
      const initialMinTextScore = normalizeAcceptanceThresholdString(
        initial?.minTextScore ?? initial?.min_text_score,
        DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.textScore
      );
      const initialMinLayoutScore = normalizeAcceptanceThresholdString(
        initial?.minLayoutScore ?? initial?.min_layout_score,
        DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.layoutScore
      );
      const initialMinAlignmentScore = normalizeAcceptanceThresholdString(
        initial?.minAlignmentScore ?? initial?.min_alignment_score,
        DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.alignmentScore
      );
      const initialMinConfidence = normalizeAcceptanceThresholdString(
        initial?.minConfidence ?? initial?.min_confidence,
        DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.confidence
      );
      const initialReasoningEffort = normalizeReasoningEffort(
        initial?.thinkingLevel
          ?? initial?.thinking_level
          ?? initial?.reasoningEffort
          ?? initial?.reasoning_effort
      );
      const initialFinalReasoningEffort = isDocumentLegalPdfToDocxAction
        ? 'none'
        : normalizeReasoningEffort(
            initial?.finalThinkingLevel
              ?? initial?.final_thinking_level
              ?? initial?.finalPassThinkingLevel
              ?? initial?.final_pass_thinking_level
              ?? initial?.finalReasoningEffort
              ?? initial?.final_reasoning_effort
              ?? initial?.finalPassReasoningEffort
              ?? initial?.final_pass_reasoning_effort
              ?? 'medium'
          );
      const initialOrientationPreset = initialOrientationPresetRaw || resolveOrientationPreset(
        initialUseEasyocr,
        initialPreviewMaxDimension,
        initialFineAngleRange,
        initialFineAngleStep
      );

      if (isRotatePdfOcrAction) {
        if (initialOrientationPreset !== orientationPreset) {
          return false;
        }

        if (orientationPreset !== 'custom') {
          return (
            initialSourceMode === sourceMode &&
            initialSourceFilePath === sourceFilePath &&
            initialTargetPath === targetPath &&
            initialRenderDpi === renderDpi &&
            initialMaxPages === maxPages
          );
        }

        return (
          initialSourceMode === sourceMode &&
          initialSourceFilePath === sourceFilePath &&
          initialTargetPath === targetPath &&
          initialRenderDpi === renderDpi &&
          initialMaxPages === maxPages &&
          initialUseEasyocr === useEasyocr &&
          initialPreviewMaxDimension === previewMaxDimension &&
          initialFineAngleRange === fineAngleRange &&
          initialFineAngleStep === fineAngleStep
        );
      }

      if (isDocumentLegalPdfToDocxAction) {
        const unchanged = (
          initialSourceMode === sourceMode &&
          initialSourceFilePath === sourceFilePath &&
          initialTargetPath === targetPath &&
          initialRenderDpi === renderDpi &&
          initialEndPage === endPage &&
          initialParallelPages === parallelPages &&
          initialMaxAttempts === maxAttempts &&
          initialIncludeInitialPageCrops === includeInitialPageCrops &&
          initialAutoOrientationPreset === autoOrientationPreset &&
          initialReasoningEffort === reasoningEffort &&
          initialMinTextScore === minTextScore &&
          initialMinLayoutScore === minLayoutScore &&
          initialMinAlignmentScore === minAlignmentScore &&
          initialMinConfidence === minConfidence
        );

        if (!unchanged) {
          const diffs = [
            ['sourceMode', initialSourceMode, sourceMode],
            ['sourceFilePath', initialSourceFilePath, sourceFilePath],
            ['targetPath', initialTargetPath, targetPath],
            ['renderDpi', initialRenderDpi, renderDpi],
            ['endPage', initialEndPage, endPage],
            ['parallelPages', initialParallelPages, parallelPages],
            ['maxAttempts', initialMaxAttempts, maxAttempts],
            ['includeInitialPageCrops', initialIncludeInitialPageCrops, includeInitialPageCrops],
            ['orientationPreset', initialAutoOrientationPreset, autoOrientationPreset],
            ['reasoningEffort', initialReasoningEffort, reasoningEffort],
            ['minTextScore', initialMinTextScore, minTextScore],
            ['minLayoutScore', initialMinLayoutScore, minLayoutScore],
            ['minAlignmentScore', initialMinAlignmentScore, minAlignmentScore],
            ['minConfidence', initialMinConfidence, minConfidence],
          ].filter(([, initialValue, currentValue]) => initialValue !== currentValue)
            .map(([field, initialValue, currentValue]) => ({ field, initialValue, currentValue }));

          console.log('[ActionsConvertPdfToDocx] customComparison mismatch', {
            id: initial?.id,
            diffs,
            diffSummary: diffs.map((item) => `${item.field}: ${JSON.stringify(item.initialValue)} -> ${JSON.stringify(item.currentValue)}`),
            values: {
              sourceMode: { initial: initialSourceMode, current: sourceMode },
              sourceFilePath: { initial: initialSourceFilePath, current: sourceFilePath },
              targetPath: { initial: initialTargetPath, current: targetPath },
              renderDpi: { initial: initialRenderDpi, current: renderDpi },
              endPage: { initial: initialEndPage, current: endPage },
              parallelPages: { initial: initialParallelPages, current: parallelPages },
              maxAttempts: { initial: initialMaxAttempts, current: maxAttempts },
              includeInitialPageCrops: { initial: initialIncludeInitialPageCrops, current: includeInitialPageCrops },
              orientationPreset: { initial: initialAutoOrientationPreset, current: autoOrientationPreset },
              reasoningEffort: { initial: initialReasoningEffort, current: reasoningEffort },
              minTextScore: { initial: initialMinTextScore, current: minTextScore },
              minLayoutScore: { initial: initialMinLayoutScore, current: minLayoutScore },
              minAlignmentScore: { initial: initialMinAlignmentScore, current: minAlignmentScore },
              minConfidence: { initial: initialMinConfidence, current: minConfidence },
            },
          });
          console.table(diffs);
          console.log('[ActionsConvertPdfToDocx] diffSummary', diffs.map((item) => `${item.field}: ${JSON.stringify(item.initialValue)} -> ${JSON.stringify(item.currentValue)}`).join(' | '));
        }

        return unchanged;
      }

      const unchanged = (
        initialSourceMode === sourceMode &&
        initialSourceFilePath === sourceFilePath &&
        initialTargetPath === targetPath &&
        initialRenderDpi === renderDpi &&
        initialMaxPages === maxPages &&
        initialIncludePageImages === includePageImages &&
        initialSaveDebugHtmlPages === saveDebugHtmlPages &&
        initialParallelPages === parallelPages &&
        initialMaxAttempts === maxAttempts &&
        initialSkipIfPdfHasText === skipIfPdfHasText &&
        initialAutoOrientationPreset === autoOrientationPreset &&
        initialReasoningEffort === reasoningEffort &&
        initialFinalReasoningEffort === finalReasoningEffort
      );


      if (!unchanged) {
        const diffs = [
          ['sourceMode', initialSourceMode, sourceMode],
          ['sourceFilePath', initialSourceFilePath, sourceFilePath],
          ['targetPath', initialTargetPath, targetPath],
          ['renderDpi', initialRenderDpi, renderDpi],
          ['maxPages', initialMaxPages, maxPages],
          ['includePageImages', initialIncludePageImages, includePageImages],
          ['saveDebugHtmlPages', initialSaveDebugHtmlPages, saveDebugHtmlPages],
          ['parallelPages', initialParallelPages, parallelPages],
          ['maxAttempts', initialMaxAttempts, maxAttempts],
          ['skipIfPdfHasText', initialSkipIfPdfHasText, skipIfPdfHasText],
          ['orientationPreset', initialAutoOrientationPreset, autoOrientationPreset],
          ['reasoningEffort', initialReasoningEffort, reasoningEffort],
          ['finalReasoningEffort', initialFinalReasoningEffort, finalReasoningEffort],
        ].filter(([, initialValue, currentValue]) => initialValue !== currentValue)
          .map(([field, initialValue, currentValue]) => ({ field, initialValue, currentValue }));

        console.log('[ActionsConvertPdfToDocx] customComparison mismatch', {
          id: initial?.id,
          diffs,
          diffSummary: diffs.map((item) => `${item.field}: ${JSON.stringify(item.initialValue)} -> ${JSON.stringify(item.currentValue)}`),
          values: {
            sourceMode: { initial: initialSourceMode, current: sourceMode },
            sourceFilePath: { initial: initialSourceFilePath, current: sourceFilePath },
            targetPath: { initial: initialTargetPath, current: targetPath },
            renderDpi: { initial: initialRenderDpi, current: renderDpi },
            maxPages: { initial: initialMaxPages, current: maxPages },
            includePageImages: { initial: initialIncludePageImages, current: includePageImages },
            saveDebugHtmlPages: { initial: initialSaveDebugHtmlPages, current: saveDebugHtmlPages },
            parallelPages: { initial: initialParallelPages, current: parallelPages },
            maxAttempts: { initial: initialMaxAttempts, current: maxAttempts },
            skipIfPdfHasText: { initial: initialSkipIfPdfHasText, current: skipIfPdfHasText },
            orientationPreset: { initial: initialAutoOrientationPreset, current: autoOrientationPreset },
            reasoningEffort: { initial: initialReasoningEffort, current: reasoningEffort },
            finalReasoningEffort: { initial: initialFinalReasoningEffort, current: finalReasoningEffort },
          },
        });
        console.table(diffs);
        console.log('[ActionsConvertPdfToDocx] diffSummary', diffs.map((item) => `${item.field}: ${JSON.stringify(item.initialValue)} -> ${JSON.stringify(item.currentValue)}`).join(' | '));
      }
      return unchanged;
    },
    customSaveData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourceFilePath.trim() : '',
      targetPath: targetPath.trim(),
      renderDpi: renderDpi.trim(),
      ...(isDocumentLegalPdfToDocxAction ? {
        parallelPages,
        endPage: endPage.trim(),
        maxAttempts: maxAttempts.trim(),
        fixAttempts: maxAttempts.trim(),
        includeInitialPageCrops,
        orientationPreset: autoOrientationPreset,
        thinkingLevel: reasoningEffort,
        minTextScore: minTextScore.trim(),
        minLayoutScore: minLayoutScore.trim(),
        minAlignmentScore: minAlignmentScore.trim(),
        minConfidence: minConfidence.trim(),
      } : {
        maxPages: maxPages.trim(),
        includePageImages,
        saveDebugHtmlPages,
        ...(usesPdfTextPipelineSettings ? {
          parallelPages,
          orientationPreset: autoOrientationPreset,
          thinkingLevel: reasoningEffort,
          ...(!isDocumentLegalPdfToDocxAction ? {
            finalThinkingLevel: finalReasoningEffort,
          } : {}),
        } : {}),
        ...(isPdfTextAction ? {
          maxAttempts: maxAttempts.trim(),
          skipIfPdfHasText,
        } : {}),
      }),
      ...(isRotatePdfOcrAction
        ? orientationPreset === 'custom'
          ? {
              orientationPreset,
              useEasyocr,
              previewMaxDimension: previewMaxDimension.trim(),
              fineAngleRange: fineAngleRange.trim(),
              fineAngleStep: fineAngleStep.trim(),
            }
          : { orientationPreset }
        : {}),
    }),
    customForceRunData: () => ({
      sourceMode,
      sourceFilePath: sourceMode === 'other' ? sourceFilePath.trim() : '',
      targetPath: targetPath.trim(),
      renderDpi: renderDpi.trim(),
      ...(isDocumentLegalPdfToDocxAction ? {
        parallelPages,
        endPage: endPage.trim(),
        maxAttempts: maxAttempts.trim(),
        fixAttempts: maxAttempts.trim(),
        includeInitialPageCrops,
        orientationPreset: autoOrientationPreset,
        thinkingLevel: reasoningEffort,
        minTextScore: minTextScore.trim(),
        minLayoutScore: minLayoutScore.trim(),
        minAlignmentScore: minAlignmentScore.trim(),
        minConfidence: minConfidence.trim(),
      } : {
        maxPages: maxPages.trim(),
        includePageImages,
        saveDebugHtmlPages,
        ...(usesPdfTextPipelineSettings ? {
          parallelPages,
          orientationPreset: autoOrientationPreset,
          thinkingLevel: reasoningEffort,
          ...(!isDocumentLegalPdfToDocxAction ? {
            finalThinkingLevel: finalReasoningEffort,
          } : {}),
        } : {}),
        ...(isPdfTextAction ? {
          maxAttempts: maxAttempts.trim(),
          skipIfPdfHasText,
        } : {}),
      }),
      ...(isRotatePdfOcrAction
        ? orientationPreset === 'custom'
          ? {
              orientationPreset,
              useEasyocr,
              previewMaxDimension: previewMaxDimension.trim(),
              fineAngleRange: fineAngleRange.trim(),
              fineAngleStep: fineAngleStep.trim(),
            }
          : { orientationPreset }
        : {}),
    }),
    loadDataFunction: (data) => {
      let parsedData = {};
      if (data.data && typeof data.data === 'string') {
        try {
          parsedData = JSON.parse(data.data);
        } catch (error) {
          console.error('Failed to parse PDF to DOCX data JSON:', error);
          return;
        }
      } else if (data.customData) {
        parsedData = data.customData;
      }

      setSourceMode(String(parsedData.sourceMode || parsedData.source_mode || defaultSourceMode));
      setSourceFilePath(String(parsedData.sourceFilePath || parsedData.sourcePath || ''));
      setTargetPath(String(parsedData.targetPath || parsedData.target || ''));
      setRenderDpi(normalizeNumberString(parsedData.renderDpi ?? parsedData.dpi, isDocumentLegalPdfToDocxAction ? '300' : '200') || (isDocumentLegalPdfToDocxAction ? '300' : '200'));
      setMaxPages(normalizeNumberString(parsedData.maxPages ?? parsedData.max_pages, ''));
      setEndPage(normalizeNumberString(parsedData.endPage ?? parsedData.end_page, ''));
      const loadedPreviewMaxDimension = normalizeNumberString(
        parsedData.previewMaxDimension ?? parsedData.preview_max_dimension,
        '1600'
      ) || '1600';
      const loadedFineAngleRange = normalizeNumberString(parsedData.fineAngleRange ?? parsedData.fine_angle_range, '7.0') || '7.0';
      const loadedFineAngleStep = normalizeNumberString(parsedData.fineAngleStep ?? parsedData.fine_angle_step, '0.5') || '0.5';
      const loadedOrientationPresetRaw = String(parsedData.orientationPreset || parsedData.orientation_preset || '').trim().toLowerCase();
      const loadedOrientationPreset = ORIENTATION_PRESETS[loadedOrientationPresetRaw]
        ? loadedOrientationPresetRaw
        : resolveOrientationPreset(
            toBoolean(parsedData.useEasyocr ?? parsedData.use_easyocr, true),
            loadedPreviewMaxDimension,
            loadedFineAngleRange,
            loadedFineAngleStep
          );
      if (loadedOrientationPreset !== 'custom') {
        applyOrientationPreset(loadedOrientationPreset);
      } else {
        setUseEasyocr(toBoolean(parsedData.useEasyocr ?? parsedData.use_easyocr, true));
        setPreviewMaxDimension(loadedPreviewMaxDimension);
        setFineAngleRange(loadedFineAngleRange);
        setFineAngleStep(loadedFineAngleStep);
        setOrientationPreset('custom');
      }
      setIncludePageImages(toBoolean(parsedData.includePageImages ?? parsedData.include_page_images, false));
      setIncludeInitialPageCrops(toBoolean(
        parsedData.includeInitialPageCrops ?? parsedData.include_initial_page_crops,
        true
      ));
      setSaveDebugHtmlPages(toBoolean(parsedData.saveDebugHtmlPages ?? parsedData.save_debug_html_pages, false));
      setParallelPages(normalizeNumberString(parsedData.parallelPages ?? parsedData.parallel_pages, isDocumentLegalPdfToDocxAction ? '8' : '1') || (isDocumentLegalPdfToDocxAction ? '8' : '1'));
      setMaxAttempts(
        normalizeNumberString(
          parsedData.fixAttempts
            ?? parsedData.fix_attempts
            ?? parsedData.maxAttempts
            ?? parsedData.max_attempts,
          isDocumentLegalPdfToDocxAction ? String(DEFAULT_LEGAL_FIX_ATTEMPTS) : String(DEFAULT_PDF_TEXT_MAX_ATTEMPTS)
        ) || (isDocumentLegalPdfToDocxAction ? String(DEFAULT_LEGAL_FIX_ATTEMPTS) : String(DEFAULT_PDF_TEXT_MAX_ATTEMPTS))
      );
      setSkipIfPdfHasText(toBoolean(parsedData.skipIfPdfHasText ?? parsedData.skip_if_pdf_has_text, isDocumentLegalPdfToDocxAction ? false : true));
      const loadedAutoOrientOcr = toBoolean(parsedData.autoOrientOcr ?? parsedData.auto_orient_ocr, false);
      setAutoOrientationPreset(normalizePdfAutoRotatePreset(
        parsedData.orientationPreset ?? parsedData.orientation_preset,
        loadedAutoOrientOcr ? 'balanced' : 'off'
      ));
      setReasoningEffort(normalizeReasoningEffort(
        parsedData.thinkingLevel
          ?? parsedData.thinking_level
          ?? parsedData.reasoningEffort
          ?? parsedData.reasoning_effort
      ));
      if (isDocumentLegalPdfToDocxAction) {
        setMinTextScore(normalizeAcceptanceThresholdString(
          parsedData.minTextScore ?? parsedData.min_text_score,
          DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.textScore
        ));
        setMinLayoutScore(normalizeAcceptanceThresholdString(
          parsedData.minLayoutScore ?? parsedData.min_layout_score,
          DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.layoutScore
        ));
        setMinAlignmentScore(normalizeAcceptanceThresholdString(
          parsedData.minAlignmentScore ?? parsedData.min_alignment_score,
          DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.alignmentScore
        ));
        setMinConfidence(normalizeAcceptanceThresholdString(
          parsedData.minConfidence ?? parsedData.min_confidence,
          DEFAULT_LEGAL_ACCEPTANCE_THRESHOLDS.confidence
        ));
      }
      setFinalReasoningEffort(
        isDocumentLegalPdfToDocxAction
          ? 'none'
          : normalizeReasoningEffort(
              parsedData.finalThinkingLevel
                ?? parsedData.final_thinking_level
                ?? parsedData.finalPassThinkingLevel
                ?? parsedData.final_pass_thinking_level
                ?? parsedData.finalReasoningEffort
                ?? parsedData.final_reasoning_effort
                ?? parsedData.finalPassReasoningEffort
                ?? parsedData.final_pass_reasoning_effort
                ?? 'medium'
            )
      );
    },
    enableConditions: true,
    initialScope,
  });

  useEffect(() => {
    updateSaveDisabledState?.();
  }, [
    sourceMode,
    sourceFilePath,
    targetPath,
    renderDpi,
    maxPages,
    endPage,
    orientationPreset,
    useEasyocr,
    previewMaxDimension,
    fineAngleRange,
    fineAngleStep,
    includePageImages,
    includeInitialPageCrops,
    saveDebugHtmlPages,
    parallelPages,
    maxAttempts,
    skipIfPdfHasText,
    autoOrientationPreset,
    reasoningEffort,
    finalReasoningEffort,
    minTextScore,
    minLayoutScore,
    minAlignmentScore,
    minConfidence,
    updateSaveDisabledState,
  ]);

  const sourceTagOptions = useMemo(
    () => [
      { key: 'filename', label: 'Filename', value: 'filename', description: 'Source filename without extension' },
      { key: 'file_extension', label: 'File Extension', value: 'file_extension', description: 'Source file extension without the dot' },
      { key: 'file_folder_path', label: 'File Folder Path', value: 'file_folder_path', description: 'Folder path of the source file' },
      { key: 'source_file_path', label: 'Source File Path', value: 'source_file_path', description: 'Absolute path to the source file' },
      { key: 'source_file_name', label: 'Source File Name', value: 'source_file_name', description: 'Source file name with extension' },
      { key: 'current_date', label: 'Current Date', value: 'current_date', description: 'Current date in YYYY-MM-DD format' },
      { key: 'current_time', label: 'Current Time', value: 'current_time', description: 'Current time in HH:MM:SS format' },
      { key: 'current_datetime', label: 'Current DateTime', value: 'current_datetime', description: 'Current date and time' },
      { key: 'projectid', label: 'Project ID', value: 'projectid', description: 'Current project identifier' },
      { key: 'project_title', label: 'Project Title', value: 'project_title', description: 'Current project title' },
      { key: 'project_description', label: 'Project Description', value: 'project_description', description: 'Current project description' },
    ],
    []
  );

  const { handleBrowseDirectory: handleBrowseSourceDirectory, FolderBrowserComponent: SourceFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        setSourceFilePath(folderPath);
        setSourcePathError('');
      }
      setSourceBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowseDisabled(false);
    },
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowser } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: false,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderClean = folderPath.replace(/[\\/]+$/, '');
        const separator = folderClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderClean}${separator}{filename}${isDocumentLegalPdfToDocxAction ? '_ocr.docx' : (isPdfTextAction ? '_text.pdf' : '_ocr.docx')}`);
        setTargetPathError('');
      }
      setTargetBrowseDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowseDisabled(false);
    },
  });

  const handleMenuOpen = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType('');
  }, []);

  const handleTagSelect = useCallback((value) => {
    if (menuType === 'source') {
      sourceRef.current?.insertTag(value);
    } else if (menuType === 'target') {
      targetRef.current?.insertTag(value);
    }
    handleMenuClose();
  }, [handleMenuClose, menuType]);

  const handleSave = useCallback(() => {
    let hasError = false;

    if (sourceMode === 'other' && !sourceFilePath.trim()) {
      setSourcePathError(
        t('pdf_to_docx.source_path_error_required', 'A source PDF path is required before saving.')
      );
      hasError = true;
    } else if (sourcePathError) {
      setSourcePathError('');
    }

    if (!targetPath.trim()) {
      setTargetPathError(targetPathRequiredError);
      hasError = true;
    } else if (targetPathError) {
      setTargetPathError('');
    }

    if (!hasError) {
      baseHandleSave();
    }
  }, [baseHandleSave, sourceFilePath, sourceMode, t, targetPath, sourcePathError, targetPathError, targetPathRequiredError]);

  const renderDpiValue = renderDpi.trim() || '200';
  const maxPagesValue = maxPages.trim();
  const sourcePathHelperText = sourcePathError
    ? sourcePathError
    : (restrictToUserFolder
        ? t(
            isRotatePdfOcrAction ? 'pdf_rotate_ocr.source_path_help' : 'pdf_to_docx.source_path_help',
            'Paths must stay within allowed folders: {allowed}',
            {
            allowed: allowedRoots?.length
              ? allowedRoots.join(', ')
              : (displayRoot || t('pdf_to_docx.source_path_root_label', 'your restricted root')),
            }
          )
        : t(
            isRotatePdfOcrAction ? 'pdf_rotate_ocr.source_path_help_any' : 'pdf_to_docx.source_path_help_any',
            'Paths must be accessible to the server.'
          ));

  return (
      <ActionSettingsLayout
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        title={resolvedTitle}
        isSaveDisabled={isSaveDisabled}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      enableConditions={true}
      popUpOpen={popUpOpen}
      forceRunResult={forceRunResult}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <PictureAsPdfIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    SOURCE
                  </Box>
                  {isRotatePdfOcrAction
            ? t('pdf_rotate_ocr.source_title', 'Page to Fix')
                    : t('pdf_to_docx.source_title', 'Source PDF')}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <FormControl component="fieldset" sx={{ mb: 2 }}>
                <RadioGroup
                  row
                  value={sourceMode}
                  onChange={(event) => {
                    setSourceMode(event.target.value);
                    if (sourcePathError) {
                      setSourcePathError('');
                    }
                  }}
                >
                  <FormControlLabel
                    value="current"
                    disabled={isProjectScope}
                    control={<Radio size="small" sx={{ color: '#5757b0', '&.Mui-checked': { color: '#5757b0' } }} />}
                    label={t('pdf_to_docx.source_mode_current', 'Current Source')}
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio size="small" sx={{ color: '#5757b0', '&.Mui-checked': { color: '#5757b0' } }} />}
                    label={t('pdf_to_docx.source_mode_other', 'Other Path')}
                  />
                </RadioGroup>
              </FormControl>

              {sourceMode === 'current' && (
                <Typography variant="body2" color="text.secondary">
                  {isRotatePdfOcrAction
                    ? t('pdf_rotate_ocr.source_mode_current_help', 'The current page will be fixed for you automatically.')
                    : t('pdf_to_docx.source_mode_current_help', 'The current event PDF will be converted automatically.')}
                </Typography>
              )}

              {sourceMode === 'other' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                    <Box sx={{ flex: 1 }}>
                      <TagEditor
                        caseid={caseid}
                        ref={sourceRef}
                        value={sourceFilePath}
                        onChange={(value) => {
                          setSourceFilePath(value);
                          if (sourcePathError) {
                            setSourcePathError('');
                          }
                        }}
                        placeholder={isRotatePdfOcrAction
                          ? t('pdf_rotate_ocr.source_path_placeholder', 'Enter the page or picture path (tags allowed)')
                          : t('pdf_to_docx.source_path_placeholder', 'Enter the source PDF path (tags allowed)')}
                        singleLine
                        tags={tags}
                        onTagClick={(event) => handleMenuOpen(event, 'source')}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<FolderOpenIcon />}
                      onClick={() => {
                        setSourceBrowseDisabled(true);
                        handleBrowseSourceDirectory();
                      }}
                      disabled={sourceBrowseDisabled}
                      sx={{
                        borderRadius: '0 8px 8px 0',
                        borderColor: '#5757b0',
                        color: '#5757b0',
                        textTransform: 'none',
                        ml: -0.5,
                        '&:hover': {
                          backgroundColor: 'rgba(87, 87, 176, 0.08)',
                          borderColor: '#5757b0',
                        },
                      }}
                    >
                      {t('common.browse', 'Browse')}
                    </Button>
                  </Box>
                  <Typography variant="caption" color={sourcePathError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                    {sourcePathHelperText}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <DescriptionIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    OUTPUT
                  </Box>
                  {targetSectionTitle}
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <TagEditor
                caseid={caseid}
                ref={targetRef}
                value={targetPath}
                onChange={(value) => {
                  setTargetPath(value);
                  if (targetPathError) {
                    setTargetPathError('');
                  }
                }}
                placeholder={targetPathPlaceholder}
                singleLine
                tags={tags}
                onTagClick={(event) => handleMenuOpen(event, 'target')}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1, mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTargetBrowseDisabled(true);
                    handleBrowseTargetDirectory();
                  }}
                  disabled={targetBrowseDisabled}
                  startIcon={<FolderOpenIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  {t('common.browse', 'Browse')}
                </Button>
              </Box>

              <Typography variant="caption" color={targetPathError ? 'error' : 'text.secondary'} sx={{ mt: 1, display: 'block' }}>
                {targetPathError || targetPathHelp}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #5757b010', overflow: 'hidden' }}>
            <CardHeader
              title={(
                <Typography sx={{ fontWeight: 700, color: '#444', fontSize: { xs: '1rem', sm: '1.1rem' }, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ bgcolor: '#5757b0', color: 'white', borderRadius: '6px', px: 1.5, py: 0.7, mr: { xs: 1, sm: 2 }, fontSize: '0.85rem', letterSpacing: '0.5px', fontWeight: 600, boxShadow: '0 3px 6px rgba(87, 87, 176, 0.2)' }}>
                    <ExpandMoreIcon sx={{ mr: 0.5, fontSize: '0.85rem' }} />
                    {t('pdf_to_docx.processing_title', 'Processing Options')}
                  </Box>
                </Typography>
              )}
              sx={{ borderBottom: '1px solid #f0f0f8', backgroundColor: '#fafbff', p: { xs: 1.5, sm: 2.5 } }}
            />
            <CardContent sx={{ pt: 2.5, pb: 2.5 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('pdf_to_docx.render_dpi_label', 'Render DPI')}
                    value={renderDpiValue}
                    onChange={(event) => setRenderDpi(event.target.value)}
                    inputProps={{ min: 72, max: 600, step: 1 }}
                  />
                </Grid>
                {showMaxPages && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('pdf_to_docx.max_pages_label', 'Max pages')}
                      value={maxPagesValue}
                      onChange={(event) => setMaxPages(event.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      placeholder={t('pdf_to_docx.max_pages_placeholder', 'Leave blank for all pages')}
                    />
                  </Grid>
                )}
                {showEndPage && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('pdf_to_docx.end_page_label', 'End page')}
                      value={endPage}
                      onChange={(event) => setEndPage(event.target.value)}
                      inputProps={{ min: 1, step: 1 }}
                      placeholder={t('pdf_to_docx.end_page_placeholder', 'Leave blank for all pages')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: 0.5 }}>
                      {t(
                        'pdf_to_docx.end_page_help',
                        'Only pages 1 through this source page are converted.'
                      )}
                    </Typography>
                  </Grid>
                )}
                {usesPdfTextPipelineSettings && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('pdf_image_to_pdf_text.parallel_pages_label', 'Parallel pages')}
                      value={parallelPages}
                      onChange={(event) => setParallelPages(event.target.value)}
                      inputProps={{ min: 1, max: 8, step: 1 }}
                      placeholder={t('pdf_image_to_pdf_text.parallel_pages_placeholder', '8')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: 0.5 }}>
                      {t(
                        'pdf_image_to_pdf_text.parallel_pages_help',
                        'Controls how many page render jobs can run at the same time inside the text-PDF pipeline.'
                      )}
                    </Typography>
                    {showMaxAttempts && (
                      <>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('pdf_image_to_pdf_text.max_attempts_label', 'Max attempts')}
                          value={maxAttempts}
                          onChange={(event) => setMaxAttempts(event.target.value)}
                          inputProps={{ min: 1, max: 8, step: 1 }}
                          sx={{ mt: 2 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: 0.5 }}>
                          {t(
                            'pdf_image_to_pdf_text.max_attempts_help',
                            'Controls how many repair retries are allowed per page. Lower values finish faster.'
                          )}
                        </Typography>
                      </>
                    )}
                  </Grid>
                )}
                {showDocumentLegalProcessingOptions && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={includeInitialPageCrops}
                          onChange={(event) => setIncludeInitialPageCrops(event.target.checked)}
                          color="primary"
                        />
                      )}
                      label={t('pdf_to_docx.include_initial_page_crops', 'Send crops in initial vision request')}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: 0.5 }}>
                      {t(
                        'pdf_to_docx.include_initial_page_crops_help',
                        'Adds the four page crops to the first render request. This can improve difficult headers, footnotes, and small text, but sends more image data.'
                      )}
                    </Typography>
                  </Grid>
                )}
                {isRotatePdfOcrAction && (
                  <Grid item xs={12}>
                      <TextField
                      fullWidth
                      select
                      label={t('pdf_rotate_ocr.preset_label', 'How careful should it be?')}
                      value={orientationPreset}
                      onChange={(event) => {
                        const nextPreset = event.target.value;
                        if (nextPreset === 'custom') {
                          setOrientationPreset('custom');
                          return;
                        }
                        applyOrientationPreset(nextPreset);
                      }}
                      sx={{ mb: 1.5 }}
                    >
                      <MenuItem value="fast">{t('pdf_rotate_ocr.preset_fast', 'Quick')}</MenuItem>
                      <MenuItem value="balanced">{t('pdf_rotate_ocr.preset_balanced', 'Balanced')}</MenuItem>
                      <MenuItem value="quality">{t('pdf_rotate_ocr.preset_quality', 'Best quality')}</MenuItem>
                      <MenuItem value="llm">{t('pdf_rotate_ocr.preset_llm', 'LLM')}</MenuItem>
                      <MenuItem value="custom">{t('pdf_rotate_ocr.preset_custom', 'More choices')}</MenuItem>
                    </TextField>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -1.0, mb: 1 }}>
                      {t(
                        'pdf_rotate_ocr.preset_help',
                        'Pick one, and the page fixer fills in the numbers for you.'
                      )}
                    </Typography>

                    {isOrientationCustom && (
                      <>
                        <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700 }}>
                          {t('pdf_rotate_ocr.advanced_title', 'More choices')}
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Switch
                              checked={useEasyocr}
                              onChange={(event) => {
                                setUseEasyocr(event.target.checked);
                                setOrientationPreset('custom');
                              }}
                            />
                          )}
                          label={t('pdf_rotate_ocr.use_easyocr', 'Let the app check the words')}
                          sx={{ mt: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                          {t(
                            'pdf_rotate_ocr.use_easyocr_help',
                            'Looks at the words to help choose the best way to turn the page. Turning it off is quicker, but less careful.'
                          )}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label={t('pdf_rotate_ocr.preview_max_dimension_label', 'Small page size')}
                              value={previewMaxDimension}
                              onChange={(event) => {
                                setPreviewMaxDimension(event.target.value);
                                setOrientationPreset('custom');
                              }}
                              inputProps={{ min: 256, max: 4096, step: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {t(
                                'pdf_rotate_ocr.preview_max_dimension_help',
                                'Makes a smaller copy for checking. Smaller is faster.'
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label={t('pdf_rotate_ocr.fine_angle_range_label', 'Tiny tilt search')}
                              value={fineAngleRange}
                              onChange={(event) => {
                                setFineAngleRange(event.target.value);
                                setOrientationPreset('custom');
                              }}
                              inputProps={{ min: 0, max: 30, step: 0.5 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {t(
                                'pdf_rotate_ocr.fine_angle_range_help',
                                'Looks for a tiny tilt around the best guess. Set to 0 to skip this.'
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              type="number"
                              label={t('pdf_rotate_ocr.fine_angle_step_label', 'Tiny tilt step')}
                              value={fineAngleStep}
                              onChange={(event) => {
                                setFineAngleStep(event.target.value);
                                setOrientationPreset('custom');
                              }}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {t(
                                'pdf_rotate_ocr.fine_angle_step_help',
                                'Smaller steps look harder but take longer.'
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
                {!isRotatePdfOcrAction && !showDocumentLegalProcessingOptions && (
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={(
                        <Switch
                          checked={includePageImages}
                          onChange={(event) => setIncludePageImages(event.target.checked)}
                        />
                      )}
                      label={t('pdf_to_docx.include_page_images', 'Embed rendered page images')}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={8}>
                  {usesPdfTextPipelineSettings && (
                    <>
                      <TextField
                        fullWidth
                        select
                        label={t('pdf_image_to_pdf_text.page_thinking_level_label', 'Per-page reasoning level')}
                        value={reasoningEffort}
                        onChange={(event) => setReasoningEffort(normalizeReasoningEffort(event.target.value))}
                        sx={{ mt: 1 }}
                      >
                        <MenuItem value="none">{t('pdf_image_to_pdf_text.thinking_none', 'None')}</MenuItem>
                        <MenuItem value="minimal">{t('pdf_image_to_pdf_text.thinking_minimal', 'Minimal')}</MenuItem>
                        <MenuItem value="low">{t('pdf_image_to_pdf_text.thinking_low', 'Low')}</MenuItem>
                        <MenuItem value="medium">{t('pdf_image_to_pdf_text.thinking_medium', 'Medium')}</MenuItem>
                        <MenuItem value="high">{t('pdf_image_to_pdf_text.thinking_high', 'High')}</MenuItem>
                        <MenuItem value="xhigh">{t('pdf_image_to_pdf_text.thinking_xhigh', 'Extra high')}</MenuItem>
                      </TextField>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                        {t(
                          'pdf_image_to_pdf_text.page_thinking_level_help',
                          'Controls the reasoning budget for each page vision and repair prompt.'
                        )}
                      </Typography>
                      {!isDocumentLegalPdfToDocxAction && (
                        <>
                          <TextField
                            fullWidth
                            select
                            label={t('pdf_image_to_pdf_text.final_thinking_level_label', 'Final pass reasoning level')}
                            value={finalReasoningEffort}
                            onChange={(event) => setFinalReasoningEffort(normalizeReasoningEffort(event.target.value))}
                            sx={{ mt: 1 }}
                          >
                            <MenuItem value="none">{t('pdf_image_to_pdf_text.thinking_none', 'None')}</MenuItem>
                            <MenuItem value="minimal">{t('pdf_image_to_pdf_text.thinking_minimal', 'Minimal')}</MenuItem>
                            <MenuItem value="low">{t('pdf_image_to_pdf_text.thinking_low', 'Low')}</MenuItem>
                            <MenuItem value="medium">{t('pdf_image_to_pdf_text.thinking_medium', 'Medium')}</MenuItem>
                            <MenuItem value="high">{t('pdf_image_to_pdf_text.thinking_high', 'High')}</MenuItem>
                            <MenuItem value="xhigh">{t('pdf_image_to_pdf_text.thinking_xhigh', 'Extra high')}</MenuItem>
                          </TextField>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                            {t(
                              'pdf_image_to_pdf_text.final_thinking_level_help',
                              'Controls only the document-wide final consistency pass. Defaults to medium.'
                            )}
                          </Typography>
                        </>
                      )}
                      <TextField
                        fullWidth
                        select
                        label={t('settings.others.pdf_auto_rotate_level', 'Rotation level')}
                        value={autoOrientationPreset}
                        onChange={(event) => setAutoOrientationPreset(normalizePdfAutoRotatePreset(event.target.value))}
                        sx={{ mt: 1 }}
                      >
                        <MenuItem value="off">{t('settings.others.pdf_auto_rotate_level_off', 'Off')}</MenuItem>
                        <MenuItem value="fast">{t('settings.others.pdf_auto_rotate_level_fast', 'Fast')}</MenuItem>
                        <MenuItem value="balanced">{t('settings.others.pdf_auto_rotate_level_balanced', 'Balanced')}</MenuItem>
                        <MenuItem value="quality">{t('settings.others.pdf_auto_rotate_level_quality', 'Quality')}</MenuItem>
                        <MenuItem value="llm">{t('settings.others.pdf_auto_rotate_level_llm', 'LLM')}</MenuItem>
                      </TextField>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                        {t(
                          'settings.others.pdf_auto_rotate_level_helper',
                          'Controls how aggressively OCR is used to normalize page orientation. Fast avoids OCR; Balanced and Quality use OCR.'
                        )}
                      </Typography>
                      {showDocumentLegalAcceptanceThresholds && (
                        <Box sx={{ mt: 2, p: 2, borderRadius: '12px', border: '1px solid #5757b010', backgroundColor: '#fafbff' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', mb: 1 }}>
                            {t('pdf_image_to_pdf_text.acceptance_thresholds_title', 'Acceptance thresholds')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                            {t(
                              'pdf_image_to_pdf_text.acceptance_thresholds_help',
                              'All four scores must meet or exceed these values before a page is accepted.'
                            )}
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('pdf_image_to_pdf_text.text_score_threshold_label', 'Text score minimum')}
                                value={minTextScore}
                                onChange={(event) => setMinTextScore(event.target.value)}
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('pdf_image_to_pdf_text.layout_score_threshold_label', 'Layout score minimum')}
                                value={minLayoutScore}
                                onChange={(event) => setMinLayoutScore(event.target.value)}
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('pdf_image_to_pdf_text.alignment_score_threshold_label', 'Alignment score minimum')}
                                value={minAlignmentScore}
                                onChange={(event) => setMinAlignmentScore(event.target.value)}
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                fullWidth
                                type="number"
                                label={t('pdf_image_to_pdf_text.confidence_threshold_label', 'Confidence minimum')}
                                value={minConfidence}
                                onChange={(event) => setMinConfidence(event.target.value)}
                                inputProps={{ min: 0, max: 1, step: 0.01 }}
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      {isPdfTextAction && (
                        <>
                          <FormControlLabel
                            control={(
                              <Switch
                                checked={skipIfPdfHasText}
                                onChange={(event) => setSkipIfPdfHasText(event.target.checked)}
                              />
                            )}
                            label={t('pdf_image_to_pdf_text.skip_if_pdf_has_text', 'Skip when source PDF already has text')}
                            sx={{ mt: 1 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5, mb: 1 }}>
                            {t(
                              'pdf_image_to_pdf_text.skip_if_pdf_has_text_help',
                              'When enabled, the action only checks whether text can be extracted from the source PDF and skips OCR if text exists.'
                            )}
                          </Typography>
                        </>
                      )}
                    </>
                  )}
                {!isRotatePdfOcrAction && !showDocumentLegalProcessingOptions && (
                    <>
                      <FormControlLabel
                        control={(
                          <Switch
                            checked={saveDebugHtmlPages}
                            onChange={(event) => setSaveDebugHtmlPages(event.target.checked)}
                          />
                        )}
                        label={t('pdf_to_docx.save_debug_html_pages', 'Save debug HTML pages to server tmp')}
                        sx={{ mt: 1 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1, mt: -0.5 }}>
                        {t(
                          'pdf_to_docx.save_debug_html_pages_help',
                          'Writes one HTML file and matching JSON debug payload per page under serverv2/tmp/pdf_to_docx_html/ for debugging and inspection.'
                        )}
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>

            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {SourceFolderBrowser}
      {TargetFolderBrowser}

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={anchorEl}
        setAnchorEl={handleMenuClose}
        menuType={menuType}
        additionalTags={sourceTagOptions}
      />
    </ActionSettingsLayout>
  );
};

export default React.memo(ActionsConvertPdfToDocx);
